import { combineServices } from "@checkout/helpers";
import {
  ChildMachineConfig,
  ParentState,
  PassengerInformationChildState,
  PassengerInformationState,
  PassportState,
} from "@checkout/types";
import { assign, createMachine, spawn } from "xstate";
import { ActionTypes, childActions } from "./actions";
import {
  ChildPassengerInformationContext,
  FlightPassengerInformationContext,
} from "./context";
import { FlightPassengerEvents, FlightPassengerEventTypes } from "./events";
import {
  FlightContext,
  FlightServices,
} from "@checkout/states/Products/Flight";
import { childGuards, GuardTypes } from "./guards";
import { services, ServiceTypes } from "./services";
import { ReviewGuardTypes } from "@checkout/states/common/Review";

export const createChildMachine = (
  initialContext: ChildPassengerInformationContext
) =>
  createMachine<ChildPassengerInformationContext, FlightPassengerEvents>(
    {
      id: ParentState.passengerInformation,
      context: { ...initialContext },
      initial: PassengerInformationState.loading,
      states: {
        [PassengerInformationState.loading]: {
          initial: PassengerInformationChildState.fetch,
          states: {
            [PassengerInformationChildState.fetch]: {
              invoke: {
                src: ServiceTypes.fetchUserPassengersService,
                onDone: {
                  target: `#${ParentState.passengerInformation}.${PassengerInformationState.loading}.${PassengerInformationChildState.route}`,
                  actions: [
                    ActionTypes.setUserPassengers,
                    ActionTypes.setSingleDefaultPassenger,
                  ],
                },
                onError: {
                  target: `#${ParentState.passengerInformation}.${PassengerInformationState.error}`,
                  actions: ActionTypes.setPassengersError,
                },
              },
            },
            [PassengerInformationChildState.route]: {
              always: [
                {
                  target: `#${ParentState.passengerInformation}.${PassengerInformationState.passengerPicker}`,
                  cond: GuardTypes.haveAnyPassengersLoaded,
                },
                {
                  target: `#${ParentState.passengerInformation}.${PassengerInformationState.passengerForm}`,
                },
              ],
            },
          },
        },
        [PassengerInformationState.passengerPicker]: {
          initial: PassengerInformationChildState.idle,
          states: {
            [PassengerInformationChildState.idle]: {},
            [PassengerInformationChildState.delete]: {
              invoke: {
                src: ServiceTypes.deleteUserPassengersService,
                onDone: {
                  actions: ActionTypes.handleDeletePassenger,
                  target: `#${ParentState.passengerInformation}.${PassengerInformationState.passengerPicker}.${PassengerInformationChildState.idle}`,
                },
                onError: {
                  actions: ActionTypes.setPassengersError,
                  target: `#${ParentState.passengerInformation}.${PassengerInformationState.error}`,
                },
              },
            },
            [PassengerInformationChildState.validate]: {
              invoke: {
                src: (ctx, e) =>
                  combineServices([
                    services.validateUserPassengersService(ctx, e),
                    FlightServices.fetchTripPricingService(ctx),
                  ]),
                onDone: [
                  {
                    actions: ActionTypes.onFetchTripPricing,
                    target: `#${ParentState.passengerInformation}.${PassengerInformationState.passengerPicker}.${PassengerInformationChildState.idle}`,
                  },
                ],
                onError: {
                  target: `#${ParentState.passengerInformation}.${PassengerInformationState.error}`,
                  actions: [
                    ActionTypes.dismissNumPassengerAlert,
                    ActionTypes.onValidateAndTripPricingError,
                  ],
                },
              },
            },
          },
          on: {
            [FlightPassengerEventTypes.SELECT_PASSENGER]: [
              {
                actions: ActionTypes.selectPassenger,
                target: `#${ParentState.passengerInformation}.${PassengerInformationState.passengerPicker}.${PassengerInformationChildState.validate}`,
                cond: GuardTypes.willHaveSelectedPassengers,
              },
              {
                actions: ActionTypes.selectPassenger,
              },
            ],
            [FlightPassengerEventTypes.NEXT]: {
              target: `#${ParentState.passengerInformation}.${PassengerInformationState.validate}`,
              cond: GuardTypes.hasAnySelectedPassengers,
            },
          },
        },
        [PassengerInformationState.passengerForm]: {
          initial: PassengerInformationChildState.route,
          states: {
            [PassengerInformationChildState.route]: {
              always: [
                {
                  cond: GuardTypes.hasCurrentUserPassenger,
                  target: PassengerInformationChildState.edit,
                },
                {
                  target: PassengerInformationChildState.new,
                },
              ],
            },
            [PassengerInformationChildState.edit]: {
              invoke: {
                src: ServiceTypes.trackEditTravelers,
                onDone: { target: PassengerInformationChildState.idle },
                onError: { target: PassengerInformationChildState.idle },
              },
            },
            [PassengerInformationChildState.new]: {
              invoke: {
                src: ServiceTypes.trackNewTraveler,
                onDone: { target: PassengerInformationChildState.idle },
                onError: { target: PassengerInformationChildState.idle },
              },
            },
            [PassengerInformationChildState.idle]: {},
            [PassengerInformationChildState.update]: {
              invoke: {
                src: ServiceTypes.updateUserPassengerService,
                onDone: {
                  target: `#${ParentState.passengerInformation}.${PassengerInformationState.passengerPicker}.${PassengerInformationChildState.validate}`,
                  actions: [
                    ActionTypes.setUserPassengers,
                    ActionTypes.selectPassenger,
                  ],
                },
                onError: {
                  target: `#${ParentState.passengerInformation}.${PassengerInformationState.error}`,
                  actions: ActionTypes.setPassengersError,
                },
              },
            },
          },
          on: {
            [FlightPassengerEventTypes.UPDATE_PASSENGER]: {
              target: `#${ParentState.passengerInformation}.${PassengerInformationState.passengerForm}.${PassengerInformationChildState.update}`,
            },
          },
        },
        [PassengerInformationState.validate]: {
          invoke: {
            src: (ctx, e) =>
              combineServices([
                services.validateUserPassengersService(ctx, e),
                FlightServices.fetchTripPricingService(ctx),
              ]),
            onDone: [
              {
                actions: [
                  ActionTypes.onFetchTripPricing,
                  ActionTypes.passengerInformationComplete,
                ],
                target: `#${ParentState.passengerInformation}.${PassengerInformationState.passengerPicker}`,
              },
            ],
            onError: {
              target: `#${ParentState.passengerInformation}.${PassengerInformationState.error}`,
              actions: [
                ActionTypes.dismissNumPassengerAlert,
                ActionTypes.onValidateAndTripPricingError,
              ],
            },
          },
        },
        [PassengerInformationState.error]: {},
      },
      on: {
        [FlightPassengerEventTypes.DELETE_PASSENGER]: {
          target: `#${ParentState.passengerInformation}.${PassengerInformationState.passengerPicker}.${PassengerInformationChildState.delete}`,
        },
        [FlightPassengerEventTypes.SET_CURRENT_PASSENGER]: {
          actions: ActionTypes.setCurrentPassenger,
        },
        [FlightPassengerEventTypes.CLEAR_PASSENGER_INFORMATION_ERROR]: {
          actions: ActionTypes.clearPassengerInformationError,
          target: `#${ParentState.passengerInformation}.${PassengerInformationState.passengerPicker}`,
        },
        [FlightPassengerEventTypes.OPEN_PASSENGER_PICKER]: `#${ParentState.passengerInformation}.${PassengerInformationState.passengerPicker}`,
        [FlightPassengerEventTypes.OPEN_PASSPORT_FORM]: {
          actions: ActionTypes.openPassportInParent,
        },
        [FlightPassengerEventTypes.OPEN_PASSENGER_FORM]: `#${ParentState.passengerInformation}.${PassengerInformationState.passengerForm}`,
        [FlightPassengerEventTypes.CLEAR_CURRENT_INFANT_ID]: {
          actions: ActionTypes.clearCurrentInfantId,
        },
        [FlightPassengerEventTypes.ON_INFANT_MODAL_CONTINUE]: {
          actions: ActionTypes.onInfantModalContinue,
          target: `#${ParentState.passengerInformation}.${PassengerInformationState.passengerPicker}.${PassengerInformationChildState.validate}`,
        },
        [FlightPassengerEventTypes.PREVIOUS]: {
          actions: ActionTypes.goPrevious,
        },
        [FlightPassengerEventTypes.OPEN_FORM_AND_SET_PASSENGER]: {
          actions: ActionTypes.setCurrentPassenger,
          target: `#${ParentState.passengerInformation}.${PassengerInformationState.passengerForm}`,
        },
      },
    },
    {
      //TODO: fix types
      actions: { ...(childActions as any) },
      services: { ...(services as any) },
      guards: { ...(childGuards as any) },
    }
  );

export const getMachineState = (config: ChildMachineConfig) => ({
  entry: assign({
    [ParentState.passengerInformation]: (ctx: FlightContext, _) => {
      const newPassengerContext = { ...ctx[ParentState.passengerInformation] };

      // Persist state for the duration of the parent machine
      if (!ctx[ParentState.passengerInformation]?.ref) {
        return {
          ...(ctx[ParentState.passengerInformation] || {}),
          ref: spawn(
            createChildMachine({
              userPassengers:
                ctx[ParentState.passengerInformation].userPassengers,
              selectedPassengerIds:
                ctx[ParentState.passengerInformation].selectedPassengerIds,
              selectedLapInfantIds:
                ctx[ParentState.passengerInformation].selectedLapInfantIds,
              currentUserPassenger:
                ctx[ParentState.passengerInformation].currentUserPassenger,
              addPassport: ctx[ParentState.passengerInformation].addPassport,
              infantOnlyError:
                ctx[ParentState.passengerInformation].infantOnlyError,
              isValidated: false,
              numPassengerAlertDismissed: false,
              // TODO: Remove below when removing child machine
              selectedTrip: ctx.flightShop.selectedTrip,
              tripDetails: ctx.flightShop.tripDetails,
            }),
            ParentState.passengerInformation
          ),
        } as FlightPassengerInformationContext;
      }
      return newPassengerContext;
    },
  }),

  always: {
    actions: ActionTypes.setPassengerVisited,
    cond: GuardTypes.notVisitedPassenger,
  },

  on: {
    [FlightPassengerEventTypes.OPEN_PASSPORT_FORM]: {
      target: `#${ParentState.passport}.${PassportState.idle}`,
      actions: ActionTypes.setPassengerContextFromChild,
    },
    [FlightPassengerEventTypes.FETCH_TRIP_PRICING]: {
      actions: ActionTypes.setTripPricing,
    },
    [FlightPassengerEventTypes.PREVIOUS]: {
      target: config?.previousState || ParentState.contactInformation,
    },
    [FlightPassengerEventTypes.COMPLETE]: [
      {
        cond: ReviewGuardTypes.visitedReview,
        target: config.nextStateFromReview || ParentState.cartQuote,
        actions: ActionTypes.setPassengerContextFromChild,
      },
      {
        target: config.nextState || ParentState.passengerInformation,
        actions: ActionTypes.setPassengerContextFromChild,
      },
    ],
  },
});
